/**
 * Web component for Anear user avatar display
 *
 * Usage:
 *   <anear-user-avatar></anear-user-avatar>
 *
 * The component will be initialized by useAppDisplayDomSetup with:
 * - avatarUrl: The URL of the user's avatar
 * - userName: The name of the user
 *
 * The component renders an <img> element internally, and falls back to an
 * initial when the URL is missing or the image fails to load.
 */
import { avatarInitial, normalizeAvatarSrc } from '../Utils/avatarSrc'

class AnearUserAvatar extends HTMLElement {
  constructor() {
    super()
    this._img = null
    this._fallback = null
    this._avatarUrl = null
    this._userName = null
  }

  connectedCallback() {
    this.render()
  }

  /**
   * Initialize the component with avatar data
   * Called by useAppDisplayDomSetup
   */
  initialize(avatarUrl, userName) {
    this._avatarUrl = avatarUrl
    this._userName = userName
    this.render()
  }

  render() {
    const src = normalizeAvatarSrc(this._avatarUrl)
    const initial = avatarInitial(this._userName)

    if (!this._img) {
      this._img = document.createElement('img')
      Array.from(this.attributes).forEach(attr => {
        if (attr.name !== 'src' && attr.name !== 'alt') {
          this._img.setAttribute(attr.name, attr.value)
        }
      })
      this._img.addEventListener('error', () => this._showFallback())
      this.appendChild(this._img)
    }

    if (!this._fallback) {
      this._fallback = document.createElement('span')
      this._fallback.setAttribute('aria-hidden', 'true')
      this.appendChild(this._fallback)
    }

    this._fallback.textContent = initial

    if (this._userName) {
      this._img.alt = `Avatar for ${this._userName}`
    }

    if (src) {
      this._fallback.hidden = true
      this._img.hidden = false
      this._img.src = src
    } else {
      this._showFallback()
    }
  }

  _showFallback() {
    if (this._img) this._img.hidden = true
    if (this._fallback) this._fallback.hidden = false
  }
}

if (!customElements.get('anear-user-avatar')) {
  customElements.define('anear-user-avatar', AnearUserAvatar)
}

export default AnearUserAvatar
